import type {
  ChatMessage,
  ConversationWithDetails,
  ParticipantWithDetails,
} from "@web-app/api";

import type { ChatMessageAttachment } from "../../api/services/chat";

/** Deliberately not a `type` check: an escalated Wingman thread still has
 * the same AI participant. */
export function hasAIParticipant(
  conversation: ConversationWithDetails,
): boolean {
  return (
    conversation.participantsWithDetails?.some((p) => p.role === "AI_AGENT") ??
    false
  );
}

export type WingmanMode = "ai" | "human";

/** PENDING counts as human: the task is queued, so the AI must stay quiet
 * even before an agent picks it up. */
export function getWingmanMode(
  conversation: ConversationWithDetails,
): WingmanMode {
  const status = conversation.conversation.providerState?.status;
  return status === "ESCALATED" || status === "PENDING" ? "human" : "ai";
}

export function getOtherParticipants(
  conversation: ConversationWithDetails,
  currentUserId: string,
): ParticipantWithDetails[] {
  return conversation.participantsWithDetails
    .filter((p) => p.userId !== currentUserId)
    .sort((a, b) => {
      if (a.role === "DRIVER" && b.role !== "DRIVER") return -1;
      if (a.role !== "DRIVER" && b.role === "DRIVER") return 1;
      return 0;
    });
}

export function getConversationDisplayName(
  conversation: ConversationWithDetails,
  currentUserId: string,
): string {
  const otherParticipants = getOtherParticipants(conversation, currentUserId);
  const isOrderScoped =
    !!conversation.orderDisplayId || !!conversation.orderExternalId;

  // Check if this is a Wingman conversation (Twilio Flex)
  if (conversation.conversation.type === "twilioFlex") {
    return "Wingman";
  }

  if (otherParticipants.length === 0) {
    return "You";
  }

  if (isOrderScoped) {
    const driver = otherParticipants.find((p) => p.role === "DRIVER");
    return driver?.name || otherParticipants[0].name;
  }

  if (otherParticipants.length === 1) {
    return otherParticipants[0].name;
  } else if (otherParticipants.length === 2) {
    return `${otherParticipants[0].name} and ${otherParticipants[1].name}`;
  } else {
    return `${otherParticipants[0].name} and ${otherParticipants.length - 1} others`;
  }
}

export function getConversationDisplayImage(
  conversation: ConversationWithDetails,
  currentUserId: string,
): string {
  // Check if this is a Wingman conversation (Twilio Flex)
  if (conversation.conversation.type === "twilioFlex") {
    return "/images/logo.png";
  }

  const otherParticipants = getOtherParticipants(conversation, currentUserId);
  return otherParticipants[0]?.image || "";
}

export function getConversationDisplayPhone(
  conversation: ConversationWithDetails,
  currentUserId: string,
): string {
  const otherParticipants = getOtherParticipants(conversation, currentUserId);
  return otherParticipants[0]?.phone || "";
}

export function hasOtherPartyRead(
  conversation: ConversationWithDetails,
  currentUserId: string,
): boolean {
  const otherParticipants = getOtherParticipants(conversation, currentUserId);

  if (otherParticipants.length === 0) {
    return true;
  }

  return otherParticipants.every((p) => p.hasReadLast);
}

export function findConversationByParticipant(
  conversations: ConversationWithDetails[],
  participantUserId: string,
): ConversationWithDetails | undefined {
  return conversations.find((conv) =>
    conv.participantsWithDetails.some((p) => p.userId === participantUserId),
  );
}

function formatCallDuration(seconds?: number): string {
  if (seconds === undefined) return "";
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes}:${String(secs).padStart(2, "0")}`;
}

export const FALLBACK_WAVEFORM_BARS = Array.from({ length: 40 }, (_, i) =>
  Math.round((Math.sin(i * 1.7) * 0.5 + 0.5) * 85 + 15),
);

export function formatVoiceTime(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "0:00";
  const whole = Math.floor(totalSeconds);
  const m = Math.floor(whole / 60);
  const s = whole % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function pickVoiceMimeType(): {
  extension: string;
  mimeType: string;
} {
  const candidates: Array<{ extension: string; mimeType: string }> = [
    { extension: "m4a", mimeType: "audio/mp4" },
    { extension: "webm", mimeType: "audio/webm;codecs=opus" },
    { extension: "webm", mimeType: "audio/webm" },
    { extension: "ogg", mimeType: "audio/ogg;codecs=opus" },
  ];
  for (const c of candidates) {
    if (
      typeof MediaRecorder !== "undefined" &&
      MediaRecorder.isTypeSupported(c.mimeType)
    ) {
      return c;
    }
  }
  return { extension: "webm", mimeType: "" };
}

/**
 * Peak-based downsample used at send time: always returns exactly `bars`
 * values, with zeros when input is empty. Output rounded to integers so it
 * serializes compactly over the wire.
 */
export function downsampleWaveform(samples: number[], bars: number): number[] {
  if (samples.length === 0) return Array.from({ length: bars }, () => 0);
  const result: number[] = [];
  const segmentSize = Math.max(1, samples.length / bars);
  for (let i = 0; i < bars; i++) {
    const start = Math.floor(i * segmentSize);
    const end = Math.min(samples.length, Math.floor((i + 1) * segmentSize));
    let peak = 0;
    for (let j = start; j < end; j++) {
      if (samples[j] > peak) peak = samples[j];
    }
    result.push(Math.round(peak));
  }
  return result;
}

/**
 * Peak-based resample used at render time: returns the source as-is on
 * length match, pads shorter input with 15 (default bar height) up to
 * `count`, and returns `[]` on empty input so the caller can skip rendering.
 */
export function resampleWaveform(source: number[], count: number): number[] {
  if (source.length === 0) return [];
  if (source.length === count) return source;
  if (source.length < count) {
    return Array.from({ length: count }, (_, i) =>
      i < source.length ? source[i] : 15,
    );
  }
  const out: number[] = [];
  for (let i = 0; i < count; i++) {
    const start = Math.floor((i * source.length) / count);
    const end = Math.max(
      start + 1,
      Math.floor(((i + 1) * source.length) / count),
    );
    let peak = 0;
    for (let j = start; j < end && j < source.length; j++) {
      if (source[j] > peak) peak = source[j];
    }
    out.push(peak);
  }
  return out;
}

export function ratioFromPointer(clientX: number, rect: DOMRect): number {
  if (rect.width <= 0) return 0;
  return Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
}

export function buildVoiceFileName(extension: string): string {
  const uuid =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `voice_${uuid}.${extension}`;
}

export function isAudioAttachment(attachment: ChatMessageAttachment): boolean {
  return (
    attachment.fileType?.startsWith("audio/") === true ||
    attachment.audioWaveform !== undefined ||
    attachment.audioDurationMs !== undefined
  );
}

// ───────────────────────────────────────────────────────────────────────────

export function getLastMessagePreview(
  lastMessage: ChatMessage | null,
  currentUserId: string,
): string {
  if (!lastMessage) return "No messages yet";

  if (lastMessage.messageType === "CALL") {
    if (lastMessage.callStatus === "ONGOING") return "Ongoing call...";
    if (lastMessage.callStatus === "ENDED") {
      const isOwnMessage = lastMessage.senderId === currentUserId;
      const userJoinedCall = lastMessage.callParticipants?.some(
        (p) => p.userId === currentUserId,
      );
      if (!isOwnMessage && !userJoinedCall) {
        return "Missed Call";
      }
      const duration = formatCallDuration(lastMessage.callDuration);
      return duration ? `Call Ended (${duration})` : "Call Ended";
    }
    return "Call";
  }

  if (lastMessage.content) return lastMessage.content;
  if (lastMessage.attachments?.length) {
    return lastMessage.senderId === currentUserId
      ? "Sent an attachment"
      : "Received an attachment";
  }
  return "No messages yet";
}
